import { useCallback, useMemo } from 'react';
import type { UserIDProps } from '@yandex-lego/user';
import { get } from '@direct-frontend/stdlib';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { FEATURE_HINT_THEME_SWITCHER_DISMISSED_KEY, persistentStorage } from '../../../../lib/storage';
import { getThemeColor, setThemeMode, type ThemeMode, useThemeColor, useThemeMode } from '../../../../lib/theme';
import { trackGoal } from '../../../../lib/trackGoal';

type UseYandexIdUserBarItemParams = {
    platform: 'desktop' | 'touch';
    dialogClassName?: string;
};

const AVATAR_SIZE = 28;
const ACCOUNT_SWITCHED = 'account_switched';

const isThemeMode = (theme: NonNullable<UserIDProps['theme']>): theme is ThemeMode =>
    theme === 'light' || theme === 'dark' || theme === 'system';

const handleYandexIdAccountChange: NonNullable<UserIDProps['onMenuItemClick']> = (item, payload) => {
    if (item === 'account-action' && get(payload, ['action']) === 'change') {
        trackGoal(ACCOUNT_SWITCHED);
    }
};

export const useYandexIdUserBarItem = ({ platform, dialogClassName }: UseYandexIdUserBarItemParams): UserIDProps => {
    const { mode } = useThemeMode();
    const colorScheme = useThemeColor();

    const handleThemeChange = useCallback((theme: NonNullable<UserIDProps['theme']>) => {
        if (!isThemeMode(theme)) {
            return;
        }

        setThemeMode(theme);
        persistentStorage.setItem(FEATURE_HINT_THEME_SWITCHER_DISMISSED_KEY, '1');
        logEventString('Theme.ModeChange', theme, {
            mode: theme,
            color: getThemeColor(),
        });
    }, []);

    return useMemo(() => {
        const baseProps: UserIDProps = {
            fetchUserData: true,
            avatarSize: AVATAR_SIZE,
            platform,
            onMenuItemClick: handleYandexIdAccountChange,
            ...(dialogClassName === undefined ? {} : { dialogClassName }),
        };

        return {
            ...baseProps,
            theme: mode,
            colorScheme,
            onThemeChange: handleThemeChange,
            features: platform === 'touch' ? (['touch_skin'] as const) : (['skin'] as const),
        };
    }, [colorScheme, dialogClassName, mode, handleThemeChange, platform]);
};
